/**
 * ChargeNowModal — Universal payment collection overlay.
 *
 * Evaluates the full payment context before the operator clicks:
 *   - Has active card? → will charge off-session (green path)
 *   - Existing payment link available? → will resend it (blue path)
 *   - No card, no link? → will generate + send new Stripe Checkout link (blue path)
 *
 * Props
 * ─────
 *   isOpen        bool    — controls visibility
 *   onClose       fn      — called on close / cancel
 *   companyId     str     — required
 *   leadId        str     — required
 *   customerName  str     — display name (default: 'Client')
 *   invoice       obj?    — pre-populated invoice; amount is read-only, route uses invoice state
 *   quoteId       str?    — optional; used for Stripe Checkout success-redirect URL
 *   onSuccess     fn?     — called with result after successful charge or link send
 */

import React, { useState, useEffect, useMemo } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  CreditCard, Send, DollarSign,
  CheckCircle2, RotateCcw, AlertTriangle, Sparkles, Coins,
} from 'lucide-react';
import { toast } from 'sonner';
import { LIFECYCLE_LABELS } from '../utils/accountLifecycle';
import { isOverrideActive } from '../utils/effectiveNextServicePrice';

// ── Route resolver ─────────────────────────────────────────────────────────────
/**
 * Determine the executable payment path given current context.
 * Returns a route descriptor the modal renders as a preview.
 */
function resolveRoute(card, invoice, amount, opts = {}) {
  const amt = parseFloat(amount) || 0;
  const {
    isCreditOnly = false, creditAmount = 0,
    isDiscountOnly = false, discountAmount = 0,
    stripeMinCollision = false,
    discountReasonRequired = false, discountTooLarge = false,
  } = opts;

  if (amt <= 0)
    return { blocked: true, blockReason: 'Enter an amount to collect' };
  if (invoice?.status === 'paid')
    return { blocked: true, blockReason: 'This invoice is already paid' };
  if (discountReasonRequired)
    return { blocked: true, blockReason: 'Discount reason is required' };
  if (discountTooLarge)
    return { blocked: true, blockReason: 'Discount exceeds invoice total' };
  if (stripeMinCollision)
    return { blocked: true, blockReason: 'Final charge below Stripe $0.50 minimum — adjust' };

  // iter_224 — discount-only path skips Stripe entirely
  if (isDiscountOnly) {
    return {
      method:  'discount_only',
      label:   `Apply $${discountAmount.toFixed(2)} discount (no charge)`,
      detail:  'Invoice waived by discount alone. No Stripe call, no credit consumed.',
      cta:     `Apply $${discountAmount.toFixed(2)} discount`,
      style:   'amber',
      Icon:    Sparkles,
    };
  }

  // iter_223 — credit-only path skips Stripe entirely
  if (isCreditOnly) {
    return {
      method:  'credit_only',
      label:   `Apply $${amt.toFixed(2)} credit (no card charged)`,
      detail:  'Invoice paid entirely from account credit. No Stripe call made.',
      cta:     `Apply $${amt.toFixed(2)} credit`,
      style:   'indigo',
      Icon:    Coins,
    };
  }

  if (card?.status === 'active' && card.brand && card.last4) {
    const stripeAmt = Math.max(0, amt - creditAmount);
    return {
      method:  'card_charge',
      label:   `Charge ${card.brand} ••••${card.last4} — $${stripeAmt.toFixed(2)}` +
               (creditAmount > 0 ? ` (+ $${creditAmount.toFixed(2)} credit)` : ''),
      detail:  'Off-session charge. Amount collected immediately.',
      cta:     `Charge $${stripeAmt.toFixed(2)}`,
      style:   'green',
      Icon:    CreditCard,
    };
  }

  if (invoice?.stripePaymentLink) {
    return {
      method:  'payment_link_resend',
      label:   `Resend payment link — $${amt.toFixed(2)}`,
      detail:  'Existing Stripe link sent via SMS. Client pays at their own pace.',
      cta:     'Resend Payment Link',
      style:   'blue',
      Icon:    Send,
    };
  }

  return {
    method:  'payment_link_new',
    label:   `Generate & send payment link — $${amt.toFixed(2)}`,
    detail:  'New Stripe Checkout link created and sent via SMS.',
    cta:     'Send Payment Link',
    style:   'blue',
    Icon:    Send,
  };
}

const ROUTE_CLS = {
  green: {
    preview: 'bg-green-50 border-green-200 text-green-800',
    cta:     'bg-green-600 hover:bg-green-700 text-white',
  },
  blue: {
    preview: 'bg-blue-50 border-blue-200 text-blue-800',
    cta:     'bg-blue-600 hover:bg-blue-700 text-white',
  },
  indigo: {
    preview: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    cta:     'bg-indigo-600 hover:bg-indigo-700 text-white',
  },
  amber: {
    preview: 'bg-amber-50 border-amber-200 text-amber-800',
    cta:     'bg-amber-600 hover:bg-amber-700 text-white',
  },
};

// ── Component ──────────────────────────────────────────────────────────────────

const ChargeNowModal = ({
  isOpen,
  onClose,
  companyId,
  leadId,
  customerName = 'Client',
  invoice      = null,
  quoteId      = null,
  onSuccess    = null,
}) => {
  const [card,        setCard]        = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [amount,      setAmount]      = useState('');
  const [description, setDescription] = useState('');
  const [loading,     setLoading]     = useState(false);
  const [result,      setResult]      = useState(null);
  // Lifecycle status (read-only). Surfaces an informational callout when
  // the account is Inactive/Closed. Charge button is NEVER disabled by this.
  const [lifecycleStatus, setLifecycleStatus] = useState(null);
  // Phase 1 override callout — passive read of lead.recurrence.nextServiceOverride.
  // We do NOT prefill the amount, auto-apply, or change Charge Now behavior.
  const [activeOverride, setActiveOverride] = useState(null);
  const [recurringRate, setRecurringRate] = useState(null);
  // iter_223 Phase 1 — Account Credit. Operator opt-in toggle.
  const [creditBalanceCents, setCreditBalanceCents] = useState(0);
  const [applyCredit,        setApplyCredit]        = useState(false);
  // iter_224 — One-Time Discount (per-invoice waiver, not refundable).
  const [applyDiscount,   setApplyDiscount]   = useState(false);
  const [discountAmount,  setDiscountAmount]  = useState('');  // dollars as-typed
  const [discountReason,  setDiscountReason]  = useState('');

  const isManual      = !invoice;
  const invoiceAmount = invoice ? parseFloat(invoice.amount || 0) : null;
  const parsedAmount  = invoiceAmount !== null ? invoiceAmount : parseFloat(amount) || 0;

  // ── Fetch card-on-file status on open ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || !leadId || !companyId) return;
    setCard(null);
    setResult(null);
    setAmount('');
    setDescription('');
    setLifecycleStatus(null);
    setActiveOverride(null);
    setRecurringRate(null);
    setCreditBalanceCents(0);
    setApplyCredit(false);
    setApplyDiscount(false);
    setDiscountAmount('');
    setDiscountReason('');

    const fetchCard = async () => {
      setCardLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token      = await auth.currentUser?.getIdToken(false);
        const qs         = new URLSearchParams({ companyId });
        const res        = await fetch(
          `${backendUrl}/api/payments/card-on-file/${leadId}?${qs}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (res.ok) setCard(await res.json());
      } catch (err) {
        console.error('[ChargeNowModal] card fetch error:', err);
      } finally {
        setCardLoading(false);
      }
    };
    fetchCard();

    // Phase E: fetch lifecycle status (read-only). Best-effort — failure
    // simply hides the callout, never blocks the modal.
    const fetchLifecycle = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token      = await auth.currentUser?.getIdToken(false);
        const res        = await fetch(
          `${backendUrl}/api/leads/${leadId}/account-status`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (res.ok) {
          const data = await res.json();
          setLifecycleStatus(data.status || null);
        }
      } catch (err) {
        // Silently swallow — callout simply won't appear.
      }
    };
    fetchLifecycle();

    // Phase 1: passive lookup of next-service override. Read-only callout —
    // no prefill, no auto-consume. Override is consumed by job completion.
    const fetchOverride = async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'leads', leadId));
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const rec = data.recurrence || {};
        if (typeof rec.amount === 'number') setRecurringRate(rec.amount);
        if (isOverrideActive(rec.nextServiceOverride)) {
          setActiveOverride(rec.nextServiceOverride);
        }
      } catch (err) {
        // Silent — callout simply won't appear.
      }
    };
    fetchOverride();

    // iter_223 — fetch account credit balance for opt-in apply toggle
    const fetchCredit = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token      = await auth.currentUser?.getIdToken(false);
        const qs         = new URLSearchParams({ companyId, limit: '1' });
        const res        = await fetch(
          `${backendUrl}/api/leads/${leadId}/credits?${qs}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (res.ok) {
          const data = await res.json();
          setCreditBalanceCents(parseInt(data.creditBalanceCents) || 0);
        }
      } catch (err) { /* silent — toggle simply won't appear */ }
    };
    fetchCredit();
  }, [isOpen, leadId, companyId]);

  // iter_223 — derive credit application math (clamped client-side; server is authoritative)
  // iter_224 — discount applies BEFORE credit per spec
  const invoiceAmountCents = Math.round(parsedAmount * 100);
  const discountCents      = useMemo(() => {
    if (!applyDiscount) return 0;
    const d = parseFloat(discountAmount);
    if (!Number.isFinite(d) || d <= 0) return 0;
    return Math.min(Math.round(d * 100), invoiceAmountCents);
  }, [applyDiscount, discountAmount, invoiceAmountCents]);
  const discountedTotalCents = Math.max(0, invoiceAmountCents - discountCents);
  const creditCanCover       = Math.min(creditBalanceCents, discountedTotalCents);
  const creditAmountCents    = applyCredit ? creditCanCover : 0;
  const stripeAmountCents    = Math.max(0, discountedTotalCents - creditAmountCents);
  const isDiscountOnly       = applyDiscount && discountCents >= invoiceAmountCents && invoiceAmountCents > 0;
  const isCreditOnly         = !isDiscountOnly && applyCredit && creditAmountCents >= discountedTotalCents && discountedTotalCents > 0;
  // Stripe minimum collision: 0 < stripeAmountCents < 50 with insufficient
  // remaining credit → operator must adjust.
  const stripeMin = 50;
  const stripeMinCollision =
    stripeAmountCents > 0
    && stripeAmountCents < stripeMin
    && (creditBalanceCents - creditAmountCents) < (stripeMin - stripeAmountCents);

  const discountReasonRequired = applyDiscount && discountCents > 0 && discountReason.trim().length === 0;
  const discountTooLarge       = applyDiscount && discountCents > invoiceAmountCents;

  // ── Resolved payment route (updates live as amount / card changes) ─────
  const route = resolveRoute(card, invoice, parsedAmount, {
    isCreditOnly,
    creditAmount: creditAmountCents / 100,
    isDiscountOnly,
    discountAmount: discountCents / 100,
    stripeMinCollision,
    discountReasonRequired,
    discountTooLarge,
  });

  // ── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (route.blocked || loading || result) return;

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token      = await auth.currentUser?.getIdToken(false);
      const res        = await fetch(`${backendUrl}/api/payments/charge-now`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyId,
          leadId,
          invoiceId:    invoice?.invoiceId || null,
          quoteId:      quoteId || null,
          manualAmount: isManual ? parsedAmount : null,
          description:  isManual ? (description || null) : null,
          sendSms:      true,
          // iter_223 — account credit application (operator opt-in)
          applyCredit:  applyCredit && creditAmountCents > 0,
          creditCents:  applyCredit ? creditAmountCents : 0,
          // iter_224 — one-time discount (per-invoice waiver, not refundable)
          discountCents:  applyDiscount ? discountCents : 0,
          discountReason: applyDiscount && discountCents > 0 ? discountReason.trim() : null,
        }),
      });

      let data = {};
      try { data = await res.json(); } catch (_) {}

      if (res.ok && data.success) {
        setResult(data);
        if (data.method === 'discount') {
          toast.success(
            `Discount applied $${(data.amount || (data.oneTimeDiscountCents || 0) / 100).toFixed(2)} — invoice paid (no charge)`
          );
        } else if (data.method === 'credit') {
          toast.success(
            `Credit applied $${(data.amount || (data.creditAppliedCents || 0) / 100).toFixed(2)} — invoice paid (no card charged)`
          );
        } else if (data.method === 'card_on_file') {
          const creditPortion   = (data.creditAppliedCents || 0) / 100;
          const discountPortion = (data.oneTimeDiscountCents || 0) / 100;
          const extras = [];
          if (discountPortion > 0) extras.push(`-$${discountPortion.toFixed(2)} discount`);
          if (creditPortion > 0)   extras.push(`-$${creditPortion.toFixed(2)} credit`);
          toast.success(
            `Charged ${data.cardBrand} ••••${data.cardLast4} — $${parsedAmount.toFixed(2)}` +
              (extras.length ? ` (${extras.join(', ')})` : '')
          );
        } else {
          toast.success('Payment link sent via SMS');
        }
        onSuccess?.(data);
      } else {
        toast.error(data.detail || 'Charge failed. Please try again.');
      }
    } catch (err) {
      console.error('[ChargeNowModal] Error:', err);
      toast.error('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm w-full" data-testid="charge-now-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Charge Now
          </DialogTitle>
          <DialogDescription>
            Collect payment from <strong>{customerName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* Phase E: lifecycle warning callout — informational only, never
              blocks the operator. */}
          {lifecycleStatus && lifecycleStatus !== 'active' && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
              data-testid="charge-now-lifecycle-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This account is <strong>{LIFECYCLE_LABELS[lifecycleStatus]}</strong>. Automatic charges are suppressed — you&apos;re charging manually.
              </span>
            </div>
          )}

          {/* Phase 1 Next-Service Override — passive informational callout.
              Does NOT prefill the amount, does NOT auto-consume. The override
              is consumed by job completion, not by Charge Now. */}
          {activeOverride && (
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
              data-testid="charge-now-override-callout"
            >
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="leading-snug">
                <div>
                  Next-service override active:{' '}
                  <strong>${Number(activeOverride.amount).toFixed(2)}</strong>
                  {recurringRate != null && Number(recurringRate) > 0 && (
                    <span className="text-amber-700">
                      {' '}(recurring rate is ${Number(recurringRate).toFixed(2)})
                    </span>
                  )}
                  {activeOverride.reason && (
                    <span className="text-amber-700">{' '}· {activeOverride.reason}</span>
                  )}
                  .
                </div>
                <div className="text-amber-700 mt-0.5">
                  This callout is informational — type the amount you intend to charge.
                  The override is consumed when the visit is completed, not at charge time.
                </div>
              </div>
            </div>
          )}

          {/* Amount */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
              Amount
            </p>
            {!isManual ? (
              <div className="text-3xl font-bold text-gray-900 tabular-nums">
                ${invoiceAmount.toFixed(2)}
              </div>
            ) : (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                  $
                </span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="charge-now-amount-input"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Description — manual charges only */}
          {isManual && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                Description{' '}
                <span className="normal-case font-normal">(optional)</span>
              </p>
              <input
                type="text"
                placeholder="e.g. Final balance, deposit, service fee…"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="charge-now-description-input"
              />
            </div>
          )}

          {/* iter_224 — One-Time Discount panel (operator opt-in) */}
          {parsedAmount > 0 && (
            <div
              className={`px-3 py-2 rounded-md border text-xs ${
                applyDiscount
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
              data-testid="charge-now-discount-panel"
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  data-testid="charge-now-discount-toggle"
                />
                <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                <span className="font-medium text-amber-900">Apply one-time discount</span>
                <span className="text-[10px] text-gray-500 ml-auto">Not refundable</span>
              </label>
              {applyDiscount && (
                <div className="mt-2 space-y-2 pl-6">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-amber-800 mb-0.5">
                      Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-700 text-xs pointer-events-none">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={parsedAmount}
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-5 pr-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:border-amber-400"
                        data-testid="charge-now-discount-amount"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-amber-800 mb-0.5">
                      Reason <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="e.g. Service recovery, manager approval"
                      maxLength={500}
                      className="w-full px-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:border-amber-400"
                      data-testid="charge-now-discount-reason"
                    />
                  </div>
                  {discountReasonRequired && (
                    <p
                      className="text-[10px] text-red-700 flex items-center gap-1"
                      data-testid="charge-now-discount-reason-error"
                    >
                      <AlertTriangle className="h-3 w-3" /> Reason is required.
                    </p>
                  )}
                  {discountTooLarge && (
                    <p className="text-[10px] text-red-700">
                      Discount exceeds invoice total.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* iter_223 — Account credit apply toggle (operator opt-in) */}
          {creditBalanceCents > 0 && discountedTotalCents > 0 && (
            <div
              className={`flex items-start gap-2 px-3 py-2 rounded-md border text-xs ${
                applyCredit
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-900'
                  : 'bg-gray-50 border-gray-200 text-gray-700'
              }`}
              data-testid="charge-now-credit-panel"
            >
              <Coins className="h-4 w-4 mt-0.5 shrink-0 text-indigo-600" />
              <div className="leading-snug flex-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyCredit}
                    onChange={(e) => setApplyCredit(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    data-testid="charge-now-credit-toggle"
                  />
                  <span>
                    Apply <strong>${(creditCanCover / 100).toFixed(2)}</strong> credit
                    {creditBalanceCents > creditCanCover && (
                      <span className="text-gray-500">
                        {' '}(${((creditBalanceCents - creditCanCover) / 100).toFixed(2)} would remain)
                      </span>
                    )}
                  </span>
                </label>
                {applyCredit && (
                  <div
                    className="mt-1 text-[11px]"
                    data-testid="charge-now-credit-summary"
                  >
                    {isCreditOnly
                      ? <>Credit covers the discounted invoice — <strong>no card will be charged</strong>.</>
                      : <>Stripe will charge <strong>${(stripeAmountCents / 100).toFixed(2)}</strong> after ${(creditAmountCents / 100).toFixed(2)} credit applied.</>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* iter_224 — Preview line items (shown when discount or credit applied) */}
          {parsedAmount > 0 && (applyDiscount || applyCredit) && (
            <div
              className="px-3 py-2 rounded-md border border-gray-200 bg-white text-xs space-y-0.5"
              data-testid="charge-now-preview"
            >
              <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1">
                Preview
              </p>
              <div className="flex justify-between text-gray-700">
                <span>Invoice amount</span>
                <span className="font-mono tabular-nums">${parsedAmount.toFixed(2)}</span>
              </div>
              {discountCents > 0 && (
                <div
                  className="flex justify-between text-amber-700"
                  data-testid="charge-now-preview-discount"
                >
                  <span>Discount</span>
                  <span className="font-mono tabular-nums">−${(discountCents / 100).toFixed(2)}</span>
                </div>
              )}
              {creditAmountCents > 0 && (
                <div
                  className="flex justify-between text-indigo-700"
                  data-testid="charge-now-preview-credit"
                >
                  <span>Credit applied</span>
                  <span className="font-mono tabular-nums">−${(creditAmountCents / 100).toFixed(2)}</span>
                </div>
              )}
              <div
                className="flex justify-between border-t border-gray-100 pt-0.5 mt-0.5 font-medium text-gray-900"
                data-testid="charge-now-preview-stripe"
              >
                <span>{isDiscountOnly || isCreditOnly ? 'Final' : 'Stripe charge'}</span>
                <span className="font-mono tabular-nums">${(stripeAmountCents / 100).toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* iter_224 — Stripe minimum collision warning */}
          {stripeMinCollision && (
            <div
              className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2"
              data-testid="charge-now-stripe-min-warning"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Final Stripe charge ${(stripeAmountCents / 100).toFixed(2)} is below the
                $0.50 minimum. Increase the discount, apply more credit, or remove the discount.
              </span>
            </div>
          )}

          {/* Card-on-file status pill */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Card on File
            </p>
            {cardLoading ? (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <RotateCcw className="h-3 w-3 animate-spin" /> Checking…
              </span>
            ) : card?.status === 'active' ? (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                {card.brand} ••••{card.last4} · Active
              </span>
            ) : card?.status === 'needs_update' ? (
              <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                Needs Update
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                None
              </span>
            )}
          </div>

          {/* Payment route preview — shows the exact path that will execute */}
          {!cardLoading && (
            route.blocked ? (
              <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{route.blockReason}</span>
              </div>
            ) : (
              <div
                className={`flex items-start gap-3 p-3 border rounded-lg ${ROUTE_CLS[route.style].preview}`}
                data-testid="charge-route-preview"
              >
                <route.Icon className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold">{route.label}</p>
                  <p className="text-xs opacity-70 mt-0.5">{route.detail}</p>
                </div>
              </div>
            )
          )}

          {/* Success confirmation — shown inline, modal stays open */}
          {result && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${
                result.method === 'card_on_file'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
              data-testid="charge-now-result"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {result.method === 'card_on_file'
                ? `$${parsedAmount.toFixed(2)} charged to ${result.cardBrand} ••••${result.cardLast4}`
                : 'Payment link sent via SMS'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && !route.blocked && (
            <Button
              size="sm"
              className={ROUTE_CLS[route.style ?? 'blue'].cta}
              onClick={handleSubmit}
              disabled={loading || cardLoading}
              data-testid="charge-now-submit-btn"
            >
              {loading ? (
                <RotateCcw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <route.Icon className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Processing…' : route.cta}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChargeNowModal;
